/** Strict SVG path grammar, without browser DOM or embedded markup. */
export interface PathCommand {
  command: string;
  values: number[];
}
const arity: Record<string, number> = {
  M: 2,
  L: 2,
  H: 1,
  V: 1,
  C: 6,
  S: 4,
  Q: 4,
  T: 2,
  A: 7,
  Z: 0,
};
export function parseMotionPath(d: string): PathCommand[] | null {
  if (typeof d !== "string" || d.length > 16384 || !d.trim()) return null;
  const tokenPattern =
    /[MLHVCSQTAZmlhvcsqtaz]|[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g;
  const tokens: string[] = [];
  let previous = 0;
  for (const match of d.matchAll(tokenPattern)) {
    const gap = d.slice(previous, match.index);
    if (
      !/^[ \t\r\n,]*$/.test(gap) ||
      gap.split(",").length > 2 ||
      (gap.includes(",") &&
        (/^[a-z]$/i.test(tokens[tokens.length - 1] ?? "") ||
          /^[a-z]$/i.test(match[0])))
    )
      return null;
    tokens.push(match[0]);
    previous = match.index! + match[0].length;
  }
  if (
    !/^[ \t\r\n]*$/.test(d.slice(previous)) ||
    !/^[Mm]$/.test(tokens[0] ?? "")
  )
    return null;
  const commands: PathCommand[] = [];
  let index = 0;
  let command = "";
  while (index < tokens.length) {
    if (/^[a-z]$/i.test(tokens[index])) command = tokens[index++];
    if (!command || arity[command.toUpperCase()] === undefined) return null;
    const n = arity[command.toUpperCase()];
    if (n === 0) {
      commands.push({ command, values: [] });
      command = "";
      continue;
    }
    if (command.toUpperCase() === "A") {
      // Flags are single digits and SVG permits them to touch one another/the next coordinate.
      for (const offset of [3, 4]) {
        const token = tokens[index + offset];
        if (!token || !/^[01]/.test(token)) return null;
        if (token.length > 1)
          tokens.splice(index + offset, 1, token[0], token.slice(1));
      }
    }
    if (index + n > tokens.length) return null;
    const values = tokens.slice(index, index + n).map(Number);
    if (values.some((v) => !Number.isFinite(v) || Math.abs(v) > 1000000))
      return null;
    if (
      command.toUpperCase() === "A" &&
      (!/^[01]$/.test(tokens[index + 3]) ||
        !/^[01]$/.test(tokens[index + 4]) ||
        values[0] < 0 ||
        values[1] < 0 ||
        ![0, 1].includes(values[3]) ||
        ![0, 1].includes(values[4]))
    )
      return null;
    commands.push({ command, values });
    index += n;
    if (command === "M") command = "L";
    else if (command === "m") command = "l";
  }
  return commands.length ? commands : null;
}
export function interpolateMotionPath(
  a: string,
  b: string,
  progress: number,
): string | null {
  const first = parseMotionPath(a);
  const second = parseMotionPath(b);
  if (
    !first ||
    !second ||
    first.length !== second.length ||
    first.some((c, i) => c.command !== second[i].command)
  )
    return null;
  return first
    .map(
      (c, i) =>
        c.command +
        c.values
          .map((v, j) => {
            if (c.command.toUpperCase() === "A" && (j === 3 || j === 4))
              return progress < 0.5 ? v : second[i].values[j];
            return (
              Math.round((v + (second[i].values[j] - v) * progress) * 1e6) / 1e6
            );
          })
          .join(" "),
    )
    .join(" ");
}
interface Point {
  x: number;
  y: number;
}
const point = (x: number, y: number): Point => ({ x, y });
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
function arcPoints(from: Point, to: Point, values: number[]): Point[] {
  let [rx, ry] = values;
  const [, , angle, large, sweep] = values;
  rx = Math.abs(rx);
  ry = Math.abs(ry);
  if (!rx || !ry || (from.x === to.x && from.y === to.y)) return [to];
  // Sub-picometer radii are visually indistinguishable; bound denominators to avoid underflow.
  rx = Math.max(1e-12, rx);
  ry = Math.max(1e-12, ry);
  const phi = (angle * Math.PI) / 180;
  const c = Math.cos(phi);
  const s = Math.sin(phi);
  const dx = (from.x - to.x) / 2;
  const dy = (from.y - to.y) / 2;
  const xp = c * dx + s * dy;
  const yp = -s * dx + c * dy;
  const lambda = (xp * xp) / (rx * rx) + (yp * yp) / (ry * ry);
  if (lambda > 1) {
    rx *= Math.sqrt(lambda);
    ry *= Math.sqrt(lambda);
  }
  const denominator = rx * rx * yp * yp + ry * ry * xp * xp;
  const factor =
    (large === sweep ? -1 : 1) *
    Math.sqrt(Math.max(0, (rx * rx * ry * ry - denominator) / denominator));
  const cxp = (factor * rx * yp) / ry;
  const cyp = (-factor * ry * xp) / rx;
  const cx = c * cxp - s * cyp + (from.x + to.x) / 2;
  const cy = s * cxp + c * cyp + (from.y + to.y) / 2;
  const start = Math.atan2((yp - cyp) / ry, (xp - cxp) / rx);
  let delta = Math.atan2((-yp - cyp) / ry, (-xp - cxp) / rx) - start;
  if (!sweep && delta > 0) delta -= Math.PI * 2;
  if (sweep && delta < 0) delta += Math.PI * 2;
  return Array.from({ length: 48 }, (_, i) => {
    const t = start + (delta * (i + 1)) / 48;
    return point(
      cx + c * rx * Math.cos(t) - s * ry * Math.sin(t),
      cy + s * rx * Math.cos(t) + c * ry * Math.sin(t),
    );
  });
}
/** Flatten curves deterministically then sample by traveled distance. */
export function sampleMotionPath(
  d: string,
  progress: number,
): Point & { rotation: number } {
  const commands = parseMotionPath(d);
  if (!commands) return { x: 0, y: 0, rotation: 0 };
  const segments: { a: Point; b: Point; length: number }[] = [];
  let current = point(0, 0);
  let origin = current;
  let control: Point | null = null;
  let lastCommand = "";
  const append = (p: Point) => {
    const length = Math.hypot(p.x - current.x, p.y - current.y);
    if (length > 0) segments.push({ a: current, b: p, length });
    current = p;
  };
  for (const { command, values: v } of commands) {
    const kind = command.toUpperCase();
    const relative = command !== kind;
    const from = current;
    const p = (i: number) =>
      point(v[i] + (relative ? from.x : 0), v[i + 1] + (relative ? from.y : 0));
    let nextControl: Point | null = null;
    if (kind === "M") {
      current = p(0);
      origin = current;
    }
    if (kind === "L") append(p(0));
    if (kind === "H") append(point(v[0] + (relative ? from.x : 0), from.y));
    if (kind === "V") append(point(from.x, v[0] + (relative ? from.y : 0)));
    if (kind === "Z") append(origin);
    if (kind === "C" || kind === "S") {
      const reflected =
        control && ["C", "S"].includes(lastCommand)
          ? point(2 * from.x - control.x, 2 * from.y - control.y)
          : from;
      const a = kind === "C" ? p(0) : reflected;
      const b = kind === "C" ? p(2) : p(0);
      const end = kind === "C" ? p(4) : p(2);
      for (let i = 1; i <= 48; i++) {
        const t = i / 48;
        const u = 1 - t;
        append(
          point(
            u ** 3 * from.x +
              3 * u * u * t * a.x +
              3 * u * t * t * b.x +
              t ** 3 * end.x,
            u ** 3 * from.y +
              3 * u * u * t * a.y +
              3 * u * t * t * b.y +
              t ** 3 * end.y,
          ),
        );
      }
      nextControl = b;
    }
    if (kind === "Q" || kind === "T") {
      const reflected =
        control && ["Q", "T"].includes(lastCommand)
          ? point(2 * from.x - control.x, 2 * from.y - control.y)
          : from;
      const a = kind === "Q" ? p(0) : reflected;
      const end = kind === "Q" ? p(2) : p(0);
      for (let i = 1; i <= 48; i++) {
        const t = i / 48;
        const u = 1 - t;
        append(
          point(
            u * u * from.x + 2 * u * t * a.x + t * t * end.x,
            u * u * from.y + 2 * u * t * a.y + t * t * end.y,
          ),
        );
      }
      nextControl = a;
    }
    if (kind === "A") for (const next of arcPoints(from, p(5), v)) append(next);
    control = nextControl;
    lastCommand = kind;
  }
  const total = segments.reduce((sum, s) => sum + s.length, 0);
  let distance = Math.max(0, Math.min(1, progress)) * total;
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    if (distance <= segment.length || i === segments.length - 1) {
      const t = distance / segment.length;
      return {
        x: lerp(segment.a.x, segment.b.x, t),
        y: lerp(segment.a.y, segment.b.y, t),
        rotation:
          (Math.atan2(segment.b.y - segment.a.y, segment.b.x - segment.a.x) *
            180) /
          Math.PI,
      };
    }
    distance -= segment.length;
  }
  return { ...current, rotation: 0 };
}
